import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LogIn, ShieldAlert, AlertCircle, Mail, Eye, EyeOff } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '@/lib/api';
import { toast } from 'sonner';

export default function AdminLogin() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const error = searchParams.get('error');
  const [authConfig, setAuthConfig] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [forgotMode, setForgotMode] = useState(false);

  useEffect(() => {
    if (user) navigate('/admin');
  }, [user, navigate]);

  useEffect(() => {
    authApi.getConfig().then(res => setAuthConfig(res.data)).catch(() => setAuthConfig({ provider: 'emergent' }));
  }, []);

  const handleGoogleLogin = () => {
    if (!authConfig) return;

    if (authConfig.provider === 'google' && authConfig.google_client_id) {
      // Direct Google OAuth — production mode (Gandi)
      const redirectUri = `${window.location.origin}/api/auth/google/callback`;
      const params = new URLSearchParams({
        client_id: authConfig.google_client_id,
        redirect_uri: redirectUri,
        response_type: 'code',
        scope: 'openid email profile',
        access_type: 'offline',
        prompt: 'select_account',
      });
      window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
    } else {
      // Emergent Auth — development/preview mode
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + '/admin';
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Email et mot de passe requis');
      return;
    }
    setSubmitting(true);
    try {
      await authApi.login({ email: email.trim().toLowerCase(), password });
      if (checkAuth) await checkAuth();
      toast.success('Connexion réussie');
      navigate('/admin');
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) toast.error('Identifiants invalides');
      else if (status === 403) toast.error('Compte désactivé');
      else toast.error(err.response?.data?.detail || 'Erreur de connexion');
    }
    setSubmitting(false);
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Veuillez saisir votre adresse email');
      return;
    }
    setSubmitting(true);
    try {
      const res = await authApi.requestPasswordReset({ email: email.trim().toLowerCase() });
      toast.success(res.data.message || 'Lien de réinitialisation envoyé.');
      setForgotMode(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Une erreur est survenue.');
    }
    setSubmitting(false);
  };

  const googleEnabled = authConfig?.provider === 'google' && authConfig?.google_client_id;
  const emergentEnabled = authConfig?.provider === 'emergent';

  return (
    <div className="min-h-screen bg-cream dark:bg-[#121212] flex items-center justify-center px-4 py-10 transition-colors duration-200" data-testid="admin-login-page">
      <div className="bg-white dark:bg-[#1a1a1a] rounded-3xl p-10 shadow-lg border border-garden-800/5 dark:border-zinc-800 max-w-md w-full">
        <div className="text-center">
          <span className="font-accent text-3xl text-garden-800 dark:text-sunny block mb-2">Côté Terrasse</span>
          <h1 className="text-2xl font-heading font-bold text-garden-800 dark:text-sunny mb-2">Administration</h1>
          <p className="text-sm text-charcoal/60 dark:text-zinc-400 mb-6">
            {forgotMode ? 'Réinitialisation de mot de passe' : 'Connectez-vous pour gérer le contenu de votre site.'}
          </p>
        </div>

        {error === 'forbidden' && (
          <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900 rounded-xl p-4 mb-6 flex items-start gap-3" data-testid="login-error-forbidden">
            <ShieldAlert size={20} className="text-red-500 shrink-0 mt-0.5" />
            <p className="text-sm text-red-700 dark:text-red-400 text-left">
              Votre adresse email n'est pas autorisée à accéder à l'administration. Contactez le gérant du restaurant.
            </p>
          </div>
        )}
        {(error === 'auth' || error === 'config') && (
          <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900 rounded-xl p-4 mb-6 flex items-start gap-3" data-testid="login-error-auth">
            <AlertCircle size={20} className="text-red-500 shrink-0 mt-0.5" />
            <p className="text-sm text-red-700 dark:text-red-400 text-left">
              Erreur d'authentification. Veuillez réessayer.
            </p>
          </div>
        )}

        {forgotMode ? (
          /* Forgot password form */
          <form onSubmit={handleForgotSubmit} className="space-y-4">
            <div>
              <Label className="text-sm dark:text-zinc-300">Email</Label>
              <Input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="vous@exemple.com"
                className="border-garden-200 dark:border-zinc-800 rounded-xl mt-1 dark:bg-zinc-900 dark:text-white"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-garden-800 hover:bg-garden-900 text-white rounded-full py-6 text-base font-medium w-full"
            >
              {submitting ? 'Envoi...' : 'Envoyer le lien de réinitialisation'}
            </Button>
            <button
              type="button"
              onClick={() => setForgotMode(false)}
              className="text-xs text-garden-600 dark:text-garden-400 hover:underline w-full text-center mt-2 block"
            >
              Retour à la connexion
            </button>
          </form>
        ) : (
          /* Email + password form */
          <form onSubmit={handlePasswordLogin} className="space-y-4" data-testid="password-login-form">
            <div>
              <Label className="text-sm dark:text-zinc-300">Email</Label>
              <Input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="vous@exemple.com"
                className="border-garden-200 dark:border-zinc-800 rounded-xl mt-1 dark:bg-zinc-900 dark:text-white"
                data-testid="login-email"
              />
            </div>
            <div>
              <div className="flex justify-between items-center">
                <Label className="text-sm dark:text-zinc-300">Mot de passe</Label>
                <button
                  type="button"
                  onClick={() => setForgotMode(true)}
                  className="text-xs text-garden-600 dark:text-garden-400 hover:underline"
                >
                  Mot de passe oublié ?
                </button>
              </div>
              <div className="relative">
                <Input
                  type={showPwd ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Votre mot de passe"
                  className="border-garden-200 dark:border-zinc-800 rounded-xl mt-1 pr-10 dark:bg-zinc-900 dark:text-white"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-charcoal/40 dark:text-zinc-500 hover:text-charcoal/70"
                  aria-label={showPwd ? 'Masquer' : 'Afficher'}
                  data-testid="login-password-toggle"
                >
                  {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="password-login-submit"
              className="bg-garden-800 hover:bg-garden-900 text-white rounded-full py-6 text-base font-medium w-full"
            >
              <Mail size={18} className="mr-2" />
              {submitting ? 'Connexion...' : 'Se connecter'}
            </Button>
          </form>
        )}

        {/* Separator + Google/Emergent button */}
        {!forgotMode && (googleEnabled || emergentEnabled) && (
          <>
            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 h-px bg-garden-100 dark:bg-zinc-800" />
              <span className="text-xs text-charcoal/40 dark:text-zinc-500 uppercase tracking-wide">ou</span>
              <div className="flex-1 h-px bg-garden-100 dark:bg-zinc-800" />
            </div>

            <Button
              onClick={handleGoogleLogin}
              disabled={!authConfig}
              variant="outline"
              data-testid="admin-login-btn"
              className="border-garden-200 dark:border-zinc-800 hover:bg-garden-50 dark:hover:bg-zinc-800/30 text-garden-800 dark:text-sunny rounded-full py-6 text-base font-medium w-full"
            >
              <LogIn size={18} className="mr-2" />
              {googleEnabled ? 'Se connecter avec Google' : 'Se connecter avec Emergent'}
            </Button>
          </>
        )}

        <Link to="/" className="text-sm text-garden-600 dark:text-garden-400 hover:text-garden-800 dark:hover:text-sunny mt-6 block text-center">
          Retour au site
        </Link>
      </div>
    </div>
  );
}
